import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleCalendarSync from './GoogleCalendarSync';
import { Monitor } from 'lucide-react';
import { 
  Home,
  FolderOpen,
  CheckSquare,
  FileText,
  Settings,
  Bell,
  ClipboardList,
  Menu,
  X,
  LogOut,
  User,
  Crown,
  Star,
  Users,
  RotateCcw,
  Sun,
  Moon,
  Calendar,
  ChevronRight,
  BarChart,        // הוסף את זה
  BookOpen,     
  Lightbulb,    
  ChevronLeft,
  Briefcase
} from 'lucide-react';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [showGoogleSync, setShowGoogleSync] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { currentUser, logout, loading: authLoading, manualRefresh } = useAuth();
  const location = useLocation();
  // Dark Mode State
const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'auto');
const [dropdownOpen, setDropdownOpen] = useState(false);

useEffect(() => {
  localStorage.setItem('theme', theme);
  const root = document.documentElement;
  
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // Auto mode - by time
    const hour = new Date().getHours();
    if (hour >= 19 || hour < 6) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}, [theme]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change in mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  // Wait for auth to be loaded and user to have a role
  if (authLoading || !currentUser?.role) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-blue-600 mx-auto shadow-lg"></div>
          <p className="mt-6 text-gray-600 font-medium">טוען את המערכת...</p>
        </div>
      </div>
    );
  }

  // Menu items with role-based access
  const menuItems = [
    {
      path: '/dashboard',
      label: 'לוח בקרה',
      icon: Home,
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee']
    },
    {
      path: '/day-opening',
      label: ' פתיחת יום/מחזור ',
      icon: Sun,
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee']
    },
    {
      path: '/day-summary',
      label: 'סיכום יום/מחזור',
      icon: Moon,
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee']
    },
    {
  path: '/team-availability',
  label: 'זמינות הצוות',
  icon: Calendar,
  roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager']
},
    { 
      path: '/projects', 
      label: 'פרויקטים', 
      icon: FolderOpen, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },
    { 
      path: '/departments', 
      label: 'מחלקות', 
      icon: Users, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager'] 
    },
    { 
      path: '/teams', 
      label: 'צוותים', 
      icon: Users, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager'] 
    },
    { 
      path: '/tasks', 
      label: 'משימות', 
      icon: CheckSquare, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },
    { 
      path: '/meetings', 
      label: 'ישיבות הנהלה', 
      icon: Briefcase, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },
    { 
      path: '/daily-routines', 
      label: 'שגרות יומיות', 
      icon: RotateCcw, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },
    { 
      path: '/team-summaries', 
      label: 'סיכומי הצוות', 
      icon: Users, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager'] 
    },
    { 
      path: '/templates', 
      label: 'תבניות', 
      icon: FileText, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager'] 
    },
      {
      path: '/resources',
      label: 'ניהול משאבים',
      icon: BookOpen,
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager']
    },
    
    
    { 
      path: '/admin', 
      label: 'ניהול מערכת', 
      icon: Settings, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager'] 
    },
    // ... שאר הקישורים ...


    { 
      path: '/user-dashboards', 
      label: 'דשבורדים של עובדים', 
      icon: Users, 
      roles: ['org_admin', 'ceo', 'deputy_ceo'] 
    },
    /*{ 
      path: '/notifications', 
      label: 'התראות', 
      icon: Bell, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },*/

    { 
      path: '/suggestions', 
      label: 'הצעות ותקלות', 
      icon: Lightbulb, 
      roles: ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager', 'employee'] 
    },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    const userRole = currentUser?.role || 'employee';
    return item.roles.includes(userRole);
  });

  // Get role display info
  const getRoleDisplay = (role) => {
    switch (role) {
      case 'org_admin':
        return { 
          label: 'מנהל ראשי', 
          icon: Crown, 
          color: 'text-purple-600 bg-purple-100' 
        };
      case 'ceo':
        return { 
          label: 'מנכ"ל', 
          icon: Crown, 
          color: 'text-purple-600 bg-purple-100' 
        };
      case 'deputy_ceo':
        return { 
          label: 'סמנכ"ל', 
          icon: Crown, 
          color: 'text-purple-600 bg-purple-100' 
        };
      case 'dept_manager':
        return { 
          label: 'מנהל מחלקה', 
          icon: Star, 
          color: 'text-blue-600 bg-blue-100' 
        };
      case 'team_manager':
        return { 
          label: 'מנהל צוות', 
          icon: Star, 
          color: 'text-blue-600 bg-blue-100' 
        };
      case 'employee':
        return { 
          label: 'עובד', 
          icon: User, 
          color: 'text-green-600 bg-green-100' 
        };
      default:
        return { 
          label: 'עובד', 
          icon: User, 
          color: 'text-green-600 bg-green-100' 
        };
    }
  };

  const roleDisplay = getRoleDisplay(currentUser?.role);
  const RoleIcon = roleDisplay.icon;

  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Mobile Menu Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        ${isMobile ? 'fixed' : 'relative'}
        ${isMobile ? (sidebarOpen ? 'left-0' : '-left-64') : ''}
        ${!isMobile ? (sidebarOpen ? 'w-64' : 'w-16') : 'w-64'}
        top-0 h-full
        bg-white shadow-xl transition-all duration-300 z-50 flex flex-col
      `}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
          <h1 className={`font-bold text-xl text-white transition-all duration-300 ${!sidebarOpen && !isMobile && 'hidden'}`}>
            מערכת ניהול
          </h1>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors group"
          >
            {sidebarOpen ? (
              <ChevronLeft className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
            ) : (
              <ChevronRight className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
            )}
          </button>
        </div>

        {/* User Info */}
        {currentUser && (
          <div className="p-4 border-b bg-gradient-to-b from-gray-50 to-white">
            <div className={`transition-all duration-300 ${!sidebarOpen && !isMobile && 'flex justify-center'}`}>
              {(sidebarOpen || isMobile) ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${roleDisplay.color} shadow-sm`}>
                      <RoleIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{currentUser.name}</p>
                      <p className="text-xs text-gray-600">{roleDisplay.label}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                </div>
              ) : (
                <div className={`p-2 rounded-full ${roleDisplay.color} shadow-sm`}>
                  <RoleIcon className="h-5 w-5" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => isMobile && setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative
                  ${isActive 
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md' 
                    : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }
                `}
              >
                <Icon size={20} className={`
                  ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}
                  ${!sidebarOpen && !isMobile && 'mx-auto'}
                `} />
                
                {(sidebarOpen || isMobile) && (
                  <span className="font-medium text-sm">{item.label}</span>
                )}
                
                {/* Tooltip for collapsed sidebar (desktop only) */}
                {!sidebarOpen && !isMobile && (
                  <div className="
                    absolute left-14 bg-gray-900 text-white px-3 py-1.5 rounded-md text-sm
                    opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap
                    pointer-events-none shadow-lg
                  ">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Google Calendar Sync Button */}
        <div className="p-3 border-t bg-gradient-to-b from-white to-gray-50">
          <button
            onClick={() => setShowGoogleSync(true)}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              bg-gradient-to-r from-blue-500 to-blue-600 text-white
              hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg
              transition-all duration-200 group
              ${!sidebarOpen && !isMobile && 'justify-center'}
            `}
          >
            <Calendar size={20} className={`${!sidebarOpen && !isMobile && 'mx-auto'}`} />
            {(sidebarOpen || isMobile) && (
              <span className="font-medium text-sm">סנכרון Google Calendar</span>
            )}
            
            {/* Tooltip for collapsed sidebar (desktop only) */}
            {!sidebarOpen && !isMobile && (
              <div className="
                absolute left-14 bg-gray-900 text-white px-3 py-1.5 rounded-md text-sm
                opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap
                pointer-events-none shadow-lg
              ">
                סנכרון Google Calendar
              </div>
            )}
          </button>
        </div>

        {/* Logout */}
        <div className="p-3 border-t bg-white">
          <button
            onClick={logout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              hover:bg-red-50 hover:text-red-600 text-gray-700
              transition-all duration-200 group
              ${!sidebarOpen && !isMobile && 'justify-center'}
            `}
          >
            <LogOut size={20} className="group-hover:text-red-600" />
            {(sidebarOpen || isMobile) && (
              <span className="font-medium text-sm">יציאה</span>
            )}
            
            {/* Tooltip for collapsed sidebar (desktop only) */}
            {!sidebarOpen && !isMobile && (
              <div className="
                absolute left-14 bg-gray-900 text-white px-3 py-1.5 rounded-md text-sm
                opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap
                pointer-events-none shadow-lg
              ">
                יציאה
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        {isMobile && (
          <div className="bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={24} />
            </button>
            <h1 className="font-semibold text-gray-800">מערכת ניהול</h1>
            <div className={`p-2 rounded-full ${roleDisplay.color}`}>
              <RoleIcon className="h-4 w-4" />
            </div>
          </div>
        )}

        
        {/* Main Content Area */}
        <main className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Google Calendar Sync Modal */}
      {showGoogleSync && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-auto bg-white rounded-xl">
            <button
              onClick={() => setShowGoogleSync(false)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10 transition-all hover:scale-110"
            >
              <X size={20} />
            </button>
            <GoogleCalendarSync currentUser={currentUser} />
          </div>
        </div>
      
      )}
    </div>
    
  );
};

export default Layout;